import {DocumentNode, useQuery} from "@apollo/client";
import {
    CentralOrderListResponse,
    GET_ORDER_LISTING_FOR_CENTRAL,
} from "../graphql/queries/getOrderListingForCentral";
import {useEffect, useMemo, useState} from "react";
import {formatDateTimeRangeToISO} from "../../../../utils/dateTimeUtils";
import {OrderType} from "../../../../graphql/types/OrderType";
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";
import {buildCentralParcelQueryVariables} from "../utils/buildCentralParcelQueryVariables";

export interface ParcelQueryResult {
    parcelData: OrderType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    accessiblePages: number;
    buildQueryVariables: () => any;
}

export const useCentralParcelQuery = (
    filters: OrderFiltersType,
    currentPage: number,
    pageSize: number,
    sortField?: string | null,
    sortDirection?: 'ASC' | 'DESC' | null,
    // 调用方可传入别名回 getOrderListingForCentral 的等价查询，以切换后端 resolver（资源码不同）
    query: DocumentNode = GET_ORDER_LISTING_FOR_CENTRAL
): ParcelQueryResult => {
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [accessiblePages, setAccessiblePages] = useState(0);

    const buildQueryVariables = () =>
        buildCentralParcelQueryVariables(filters, currentPage, pageSize, sortField, sortDirection);

    const memoizedVariables = useMemo(
        () => buildQueryVariables(),
        [filters, currentPage, pageSize, sortField, sortDirection]
    );

    // 执行查询
    const { data, loading: queryLoading, refetch } = useQuery<CentralOrderListResponse>(query,
        {variables: memoizedVariables});

    // 当数据变化时更新状态
    useEffect(() => {
        const page = data?.getOrderListingForCentral;
        if (!page) {
            return;
        }
        // content 为空数组 [] 时也要落态；不能用 if (content)（虽 [] 为 truthy，但 null/undefined 时原先不会 setLoading(false)）
        const responseData = page.content ?? [];
        let orderedData = responseData;
        const trackingInput = filters.trackingNumbers;
        // 只有在没有用户主动排序时，才使用运单号顺序排序
        // 如果用户点击了列排序（sortField 不为空），则使用后端排序结果
        if (trackingInput && trackingInput.trim().length > 0 && !sortField) {
            const trackingList = trackingInput
                .split(/[\r\n,\s]+/)
                .map(t => t.trim())
                .filter(t => t.length > 0);
            if (trackingList.length > 0) {
                const orderMap = new Map<string, number>();
                trackingList.forEach((tracking, idx) => {
                    orderMap.set(tracking.toUpperCase(), idx);
                });
                orderedData = [...responseData].sort((a, b) => {
                    const aKey = (a.trackingNumber || '').toUpperCase();
                    const bKey = (b.trackingNumber || '').toUpperCase();
                    const aIndex = orderMap.has(aKey) ? (orderMap.get(aKey) as number) : Number.MAX_SAFE_INTEGER;
                    const bIndex = orderMap.has(bKey) ? (orderMap.get(bKey) as number) : Number.MAX_SAFE_INTEGER;
                    if (aIndex !== bIndex) {
                        return aIndex - bIndex;
                    }
                    return 0;
                });
            }
        }
        setParcelData(orderedData);
        setLoading(false);
        // GraphQL Long 偶发为字符串 "0"，必须 Number 后再存，否则 R4 的 totalElements === 0 恒为 false
        setTotal(Number(page.totalElements) || 0);
        setAccessiblePages(Number(page.accessiblePages) || 0);
    }, [data, filters.trackingNumbers, sortField]);

    return {
        parcelData,
        loading: loading || queryLoading,
        refetch,
        totalElements: total,
        accessiblePages,
        buildQueryVariables
    };
}; 